/**
 * Frontend copy of backend leaseHelper: generates per-period rent schedule.
 * Keep in sync with flexManagement-Backend/utils/leaseHelper.js
 *
 * @param {number} baseRent
 * @param {Array<{ amount?: string | number }>} additionalCharges
 * @param {boolean} isFirstMonthRent - first month free (grace period)
 * @param {string} startDate - "YYYY-MM-DD"
 * @param {string} endDate - "YYYY-MM-DD"
 * @param {number} annualIncrementPercentage
 * @returns {Array<{ id: string, totalRent: number, status: string, transectionId: string, fromDate: string, toDate: string }>}
 */
export function generateRentSchedule(
  baseRent,
  additionalCharges,
  isFirstMonthRent,
  startDate,
  endDate,
  annualIncrementPercentage
) {
  const result = [];

  const chargesTotal = (additionalCharges || []).reduce(
    (sum, c) => sum + Number(c.amount || 0),
    0
  );

  let currentBaseRent = Number(baseRent);

  // Normalize to YYYY-MM-DD if Date or ISO string
  const norm = (d) => {
    if (!d) return null;
    if (typeof d === "string") return d.includes("T") ? d.split("T")[0] : d;
    const date = new Date(d);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const startStr = norm(startDate);
  const endStr = norm(endDate);
  if (!startStr || !endStr) return result;

  const [startYear, startMonth, startDay] = startStr.split("-").map(Number);
  const [endYear, endMonth, endDay] = endStr.split("-").map(Number);

  let currentDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));
  const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));

  let monthIndex = 0;

  while (currentDate <= end) {
    const year = currentDate.getUTCFullYear();
    const month = currentDate.getUTCMonth();

    const fromDate =
      monthIndex === 0
        ? new Date(Date.UTC(year, month, startDay))
        : new Date(Date.UTC(year, month, 1));

    const toDate =
      currentDate.getUTCFullYear() === end.getUTCFullYear() &&
      currentDate.getUTCMonth() === end.getUTCMonth()
        ? new Date(Date.UTC(year, month, endDay))
        : new Date(Date.UTC(year, month + 1, 0));

    const totalDaysInMonth = new Date(
      Date.UTC(year, month + 1, 0)
    ).getUTCDate();
    const activeDays = toDate.getUTCDate() - fromDate.getUTCDate() + 1;

    if (monthIndex !== 0 && monthIndex % 12 === 0) {
      currentBaseRent =
        currentBaseRent + (currentBaseRent * annualIncrementPercentage) / 100;
    }

    const monthlyRent = currentBaseRent + chargesTotal;
    let totalRent = monthlyRent;

    if (monthIndex === 0 && isFirstMonthRent) {
      totalRent = 0;
    } else if (activeDays < totalDaysInMonth) {
      totalRent = (monthlyRent / totalDaysInMonth) * activeDays;
    }

    result.push({
      id: Math.random().toString(36).substring(2, 10),
      totalRent: Number(totalRent.toFixed(2)),
      status: "Pending",
      transectionId: "",
      fromDate: fromDate.toISOString().split("T")[0],
      toDate: toDate.toISOString().split("T")[0],
    });

    currentDate = new Date(Date.UTC(year, month + 1, 1));
    monthIndex++;
  }

  return result;
}
